import { isSafeExternalUrl } from '../../security/validateExternalUrl';
import type { MapData, MapWaypoint } from './types';

/**
 * Hosts a constructed or backend-supplied map URL may point to. Both are
 * legitimate Google Maps embed hosts; nothing else is ever accepted.
 */
export const GOOGLE_MAPS_EMBED_ALLOWED_HOSTS = ['www.google.com', 'maps.google.com'];

const DEFAULT_ZOOM = 12;
const MIN_ZOOM = 1;
const MAX_ZOOM = 20;

function isFiniteCoordinate(latitude: number, longitude: number): boolean {
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
}

function clampZoom(zoom: number | undefined): number {
  if (zoom === undefined || !Number.isFinite(zoom)) return DEFAULT_ZOOM;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(zoom)));
}

function waypointToQuery(waypoint: MapWaypoint | undefined): string | null {
  if (!waypoint) return null;
  if (
    waypoint.latitude !== undefined &&
    waypoint.longitude !== undefined &&
    isFiniteCoordinate(waypoint.latitude, waypoint.longitude)
  ) {
    return `${waypoint.latitude},${waypoint.longitude}`;
  }
  if (waypoint.address) return waypoint.address;
  return null;
}

/**
 * Builds a Google Maps embed URL that requires no API key (the long-standing
 * `output=embed` mechanism), entirely from structured, type-checked data —
 * never from a string Gemini could shape into something else. This is the
 * preferred path; a backend-supplied `data.url` is only ever used after
 * passing isSafeExternalUrl (see resolveMapUrl below).
 */
function buildFromStructuredData(data: MapData): string | null {
  const base = 'https://www.google.com/maps';

  const originQuery = waypointToQuery(data.origin);
  const destinationQuery = waypointToQuery(data.destination);
  if (originQuery && destinationQuery) {
    return `${base}?saddr=${encodeURIComponent(originQuery)}&daddr=${encodeURIComponent(destinationQuery)}&output=embed`;
  }

  if (data.query) {
    return `${base}?q=${encodeURIComponent(data.query)}&z=${clampZoom(data.zoom)}&output=embed`;
  }

  const point = data.center ?? data.markers?.[0];
  if (point && isFiniteCoordinate(point.latitude, point.longitude)) {
    return `${base}?ll=${point.latitude},${point.longitude}&z=${clampZoom(data.zoom)}&output=embed`;
  }

  return null;
}

/**
 * Resolves the URL to actually use for the map iframe, or null if nothing
 * safe/usable is available. A backend-supplied URL is honored only if it
 * passes strict validation; otherwise we fall back to building one
 * ourselves from whatever structured fields are present.
 */
export function resolveMapUrl(data: MapData | undefined): string | null {
  if (!data) return null;

  if (
    data.url &&
    isSafeExternalUrl(data.url, {
      allowedHosts: GOOGLE_MAPS_EMBED_ALLOWED_HOSTS,
      requireHttps: true,
    })
  ) {
    return data.url;
  }

  return buildFromStructuredData(data);
}
